var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { Box, InputLabel as MuiInputLabel, Stack, Typography, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TIMEZONE_OPTIONS } from '../../orgConfigWizard/hours/selectors';
import { Autocomplete } from '../autocomplete';
var labelBaseStyles = function (_a) {
  var theme = _a.theme;
  return {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.subtitle2.fontWeight,
    fontSize: theme.typography.subtitle2.fontSize,
    position: 'static',
    transform: 'none'
  };
};
var InlineLabel = styled(MuiInputLabel)(function (_a) {
  var theme = _a.theme;
  return __assign(__assign({}, labelBaseStyles({
    theme: theme
  })), {
    whiteSpace: 'nowrap'
  });
});
var TopLabel = styled(MuiInputLabel)(function (_a) {
  var theme = _a.theme;
  return __assign(__assign({}, labelBaseStyles({
    theme: theme
  })), {
    marginBottom: theme.spacing(1.2)
  });
});
var SelectMinWidthBox = styled(Box)({
  minWidth: 260
});
var TimezoneMenuRow = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2)
  };
});
function buildTimezoneLabel(iana, regionLabel) {
  var rest = iana.split('/').slice(1).join('/').replaceAll('_', ' ');
  return rest ? "".concat(regionLabel, "/").concat(rest) : regionLabel;
}
function getCurrentTimeAtIana(iana, now) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: iana,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(now);
  } catch (_a) {
    return '';
  }
}
export var TimezoneSelect = function (_a) {
  var value = _a.value,
    onChange = _a.onChange,
    _b = _a.size,
    size = _b === void 0 ? 'small' : _b,
    _c = _a.fullWidth,
    fullWidth = _c === void 0 ? true : _c,
    label = _a.label,
    _d = _a.labelPlacement,
    labelPlacement = _d === void 0 ? 'top' : _d,
    _e = _a.disabled,
    disabled = _e === void 0 ? false : _e,
    sx = _a.sx;
  var t = useTranslation('orgConfig', {
    keyPrefix: 'hours'
  }).t;
  var _f = useState(function () {
      return new Date();
    }),
    now = _f[0],
    setNow = _f[1];
  useEffect(function () {
    var id = setInterval(function () {
      return setNow(new Date());
    }, 60000);
    return function () {
      return clearInterval(id);
    };
  }, []);
  var getTimezoneLabel = function (iana) {
    var region = iana.split('/')[0];
    return buildTimezoneLabel(iana, t("timezone.regions.".concat(region)));
  };
  var isKnownOption = !value || TIMEZONE_OPTIONS.some(function (tz) {
    return tz.iana === value;
  });
  var options = useMemo(function () {
    var list = TIMEZONE_OPTIONS.map(function (tz) {
      return {
        id: tz.iana,
        name: getTimezoneLabel(tz.iana),
        time: getCurrentTimeAtIana(tz.iana, now)
      };
    });
    if (value && !isKnownOption) {
      list.unshift({
        id: value,
        name: getTimezoneLabel(value),
        time: getCurrentTimeAtIana(value, now)
      });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, value, isKnownOption]);
  var select = _jsx(Autocomplete, {
    sx: sx,
    size: size,
    fullWidth: fullWidth,
    disabled: disabled,
    options: options,
    value: value !== null && value !== void 0 ? value : null,
    getOptionValue: function (o) {
      return o.id;
    },
    getOptionLabel: function (o) {
      return o.time ? "".concat(o.name, " (").concat(o.time, ")") : o.name;
    },
    isOptionEqualToValue: function (option, selected) {
      return option.id === selected.id;
    },
    placeholder: t('timezone.select', {
      defaultValue: 'Select timezone'
    }),
    onChange: function (newValue) {
      var _a;
      return onChange((_a = newValue) !== null && _a !== void 0 ? _a : null);
    },
    renderOption: function (liProps, option) {
      return _createElement("li", __assign({}, liProps, {
        key: option.id
      }), _jsxs(TimezoneMenuRow, {
        children: [_jsx("span", {
          children: option.name
        }), _jsx(Typography, __assign({
          variant: "body2",
          color: "text.secondary",
          component: "span",
          sx: {
            flexShrink: 0
          }
        }, {
          children: option.time
        }))]
      }));
    }
  });
  if (!label) return select;
  if (labelPlacement === 'start') {
    return _jsxs(Stack, __assign({
      direction: "row",
      alignItems: "center",
      gap: 4
    }, {
      children: [_jsx(InlineLabel, {
        children: label
      }), _jsx(SelectMinWidthBox, {
        children: select
      })]
    }));
  }
  return _jsxs(Stack, {
    children: [_jsx(TopLabel, {
      children: label
    }), select]
  });
};